/**
 * UserPicker — V22.9.7
 *
 * Searchable typeahead bound to GET /api/users/ (returns every user in the
 * caller's tenant). Surfaces name, email and role badge in the dropdown so the
 * person picking knows whether they're assigning a Process Manager, an App
 * Manager, an IT Function Manager, etc.
 *
 * Props:
 *   api          — the api instance from useAuth()
 *   value        — { id, name, email, role } | null
 *   onChange     — (selection) => void
 *   placeholder  — string
 *   restrictToRoles — optional array of role strings to filter the list
 *                     (used by the Approver picker to show managers only)
 *   testIdSuffix — data-testid suffix
 */
import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Check, ChevronsUpDown, User } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin",
  bcm_manager: "BCM Manager",
  department_head: "Department Head",
  process_manager: "Process Mgr",
  app_manager: "App Mgr",
  it_function_manager: "IT Function Mgr",
  it_drp_manager: "IT-DRP Mgr",
  vendor_manager: "Vendor Mgr",
  contributor: "Contributor",
};

export default function UserPicker({
  api,
  value,
  onChange,
  placeholder = "Select a user…",
  restrictToRoles = null,
  testIdSuffix = "",
}) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await api.get("/users/");
        if (!cancelled) {
          const list = (res.data || []).map((u) => ({
            id: u.id,
            name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
            email: u.email,
            role: u.role,
            department: u.department,
          }));
          setUsers(list);
        }
      } catch (e) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (open) load();
    return () => { cancelled = true; };
  }, [open, api]);

  const filtered = users
    .filter((u) => !restrictToRoles || restrictToRoles.includes(u.role))
    .filter((u) =>
      !q ||
      u.name.toLowerCase().includes(q.toLowerCase()) ||
      u.email.toLowerCase().includes(q.toLowerCase())
    );

  const label = !value ? "" : value.name || value.email || "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={`user-picker-trigger-${testIdSuffix}`}
        >
          <span className="flex items-center gap-2 truncate">
            <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className={label ? "text-slate-900" : "text-slate-400"}>
              {label || placeholder}
            </span>
            {value?.role && (
              <Badge variant="outline" className="text-[10px] ml-1 bg-slate-50 text-slate-600">
                {ROLE_LABEL[value.role] || value.role}
              </Badge>
            )}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name or email…"
            value={q}
            onValueChange={setQ}
            data-testid={`user-picker-search-${testIdSuffix}`}
          />
          <CommandList>
            {loading && <div className="py-4 text-center text-xs text-slate-400">Loading…</div>}
            {!loading && filtered.length === 0 && <CommandEmpty>No matching users.</CommandEmpty>}
            {!loading && filtered.length > 0 && (
              <CommandGroup>
                {filtered.slice(0, 50).map((u) => (
                  <CommandItem
                    key={u.id}
                    value={`${u.id}-${u.email}`}
                    onSelect={() => {
                      onChange(u);
                      setOpen(false);
                      setQ("");
                    }}
                    className="flex items-center gap-2 text-sm"
                    data-testid={`user-picker-option-${u.id}-${testIdSuffix}`}
                  >
                    <Check className={`w-3.5 h-3.5 ${value?.id === u.id ? "opacity-100" : "opacity-0"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{u.name}</p>
                      <p className="truncate text-[11px] text-slate-500">{u.email}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 flex-shrink-0">
                      {ROLE_LABEL[u.role] || u.role}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
